import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { API, authHeaders } from "./adminApi";
import { PRICE_PAGES } from "../../lib/site";
import { resetSiteSettingsCache } from "../../lib/useSiteSettings";

const TRUST_FIELDS = [
  { key: "certibiocide", label: "N° Certibiocide", ph: "ex : 23-0123" },
  { key: "rc_pro", label: "Assurance RC Pro (assureur / n° de contrat)", ph: "ex : AXA n° 1234567" },
  { key: "siret", label: "SIRET (vide = celui des mentions légales)", ph: "ex : 123 456 789 00012" },
];

export default function SettingsAdmin({ token }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings`).then((r) => setForm((f) => f ?? r.data));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/settings`, form, { headers: authHeaders(token) });
      setForm(res.data);
      resetSiteSettingsCache();
      toast.success("Paramètres enregistrés");
    } catch {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <div className="text-center py-20 text-[#5C705E]">Chargement…</div>;
  const setPrice = (slug, v) => setForm({ ...form, prices: { ...form.prices, [slug]: v } });

  return (
    <div data-testid="admin-settings-page" className="space-y-8 max-w-3xl">
      <header>
        <h2 className="font-['Outfit'] text-3xl font-semibold text-[#1A2E1C]">Paramètres du site</h2>
        <p className="text-[#5C705E] mt-1">Un champ vide n'est pas affiché sur le site.</p>
      </header>

      <section className="bg-white border border-[#E5EBE5] rounded-2xl p-6 space-y-4">
        <h3 className="font-['Outfit'] font-semibold text-[#1A2E1C]">Bandeau de confiance (sous le hero de l'accueil)</h3>
        {TRUST_FIELDS.map((f) => (
          <div key={f.key}>
            <Label className="mb-1.5 block">{f.label}</Label>
            <Input value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} placeholder={f.ph} data-testid={`settings-${f.key}`} className="rounded-xl h-11" />
          </div>
        ))}
        <p className="text-xs text-[#5C705E]">« Devis gratuit sous 24h » est toujours affiché.</p>
      </section>

      <section className="bg-white border border-[#E5EBE5] rounded-2xl p-6 space-y-4">
        <h3 className="font-['Outfit'] font-semibold text-[#1A2E1C]">Tarifs indicatifs « à partir de »</h3>
        <p className="text-xs text-[#5C705E]">Saisissez seulement le montant, ex : « 90 € TTC » ou « 45 €/h ». Affiché : « Tarif indicatif : à partir de … ».</p>
        <div className="grid sm:grid-cols-2 gap-4">
          {PRICE_PAGES.map((p) => (
            <div key={p.slug}>
              <Label className="mb-1.5 block">{p.label}</Label>
              <Input value={form.prices[p.slug] || ""} onChange={(e) => setPrice(p.slug, e.target.value)} placeholder="ex : 90 €" data-testid={`settings-price-${p.slug}`} className="rounded-xl h-11" />
            </div>
          ))}
        </div>
      </section>

      <Button onClick={save} disabled={saving} data-testid="settings-save" className="rounded-full bg-[#1B5E20] hover:bg-[#164d1a] text-white px-8">
        <Save className="w-4 h-4 mr-2" /> {saving ? "Enregistrement…" : "Enregistrer"}
      </Button>
    </div>
  );
}
